import { memo, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AdminKnapp, AuthButton, useKonto } from './AuthButton.tsx';
import { kanAapneAdmin } from '../lib/aapneAdmin.ts';

// Racecloud-merkevare (marineblå + sjø-cyan + varm oransje) — jf. docs/hjemmeside-mockup.html
export const SEA = '#17a2b8';
export const SAND = '#e8782d';

const MENU = [
  { to: '/', label: 'Hjem', end: true },
  { to: '/regattaer', label: 'Regattaer' },
  { to: '/om', label: 'Om' },
];

/** Seil-/kompassmerke. */
export function BrandMark({ size = 38 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
      <rect x="2" y="2" width="44" height="44" rx="12" fill="#0a2740" />
      <path d="M24 9 L24 30 L12 30 Z" fill="#f7fbfd" />
      <path d="M26 13 L36 30 L26 30 Z" fill="#17a2b8" />
      <path d="M8 33 q8 4 16 0 t16 0" stroke="#e8782d" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <circle cx="24" cy="24" r="21.5" fill="none" stroke="rgba(255,255,255,.14)" strokeWidth="1" />
    </svg>
  );
}

/**
 * Topplinje: logo venstre, meny, innlogging høyre (Min side, og Admin for admin-kontoer).
 * På mobil ligger menyen — og Admin — bak en menyknapp.
 * memo: regattasiden rendrer hver animasjonsramme, topplinja trenger ikke følge med.
 */
const SiteHeader = memo(function SiteHeader() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const me = useKonto();
  const admin = me.isSuccess && kanAapneAdmin(me.data);
  return (
    <header className="sticky top-0 z-40 bg-slate-950/85 backdrop-blur border-b border-slate-800">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-3 sm:gap-4">
        <Link to="/" onClick={close} className="flex items-center gap-2.5 mr-auto" aria-label="Racecloud">
          <BrandMark />
          <span className="leading-none">
            <span className="block font-extrabold text-lg tracking-tight">Racecloud</span>
            <span className="hidden sm:block text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500 mt-0.5">
              Regatta · Live · Resultater
            </span>
          </span>
        </Link>
        <nav className="hidden sm:flex items-center gap-6 text-sm">
          {MENU.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              className={({ isActive }) =>
                isActive ? 'font-semibold text-sky-300' : 'text-slate-300 hover:text-white'
              }
            >
              {m.label}
            </NavLink>
          ))}
        </nav>
        <AuthButton />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Lukk meny' : 'Åpne meny'}
          aria-expanded={open}
          className="sm:hidden inline-flex items-center justify-center w-10 h-10 -mr-1 rounded-lg text-slate-200 hover:bg-slate-800"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            {open ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>
      {open && (
        <nav className="sm:hidden border-t border-slate-800 px-4 pb-1">
          {MENU.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              onClick={close}
              className={({ isActive }) =>
                `block py-3 border-b border-slate-800/70 last:border-0 ${
                  isActive ? 'font-semibold text-sky-300' : 'text-slate-300'
                }`
              }
            >
              {m.label}
            </NavLink>
          ))}
          {admin && (
            <div className="py-3">
              <AdminKnapp className="flex w-full justify-center" onClick={close} />
            </div>
          )}
        </nav>
      )}
    </header>
  );
});

const SiteFooter = memo(function SiteFooter() {
  return (
    <footer className="border-t border-slate-800 mt-4">
      <div className="max-w-6xl mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
        <div className="flex items-center gap-2.5">
          <BrandMark size={28} />
          <span className="font-semibold text-slate-300">Racecloud</span>
        </div>
        <span>© {new Date().getFullYear()} racecloud.no</span>
      </div>
    </footer>
  );
});

/**
 * Felles ramme for de offentlige nettsidene: topplinje + innhold + bunnlinje.
 * `bg` overstyrer bakgrunnen — regattasiden bruker den som statusfarge for hele siden.
 */
export function SiteLayout({ children, bg = 'bg-slate-950' }: { children: ReactNode; bg?: string }) {
  return (
    <div className={`min-h-screen flex flex-col text-slate-100 ${bg}`}>
      <SiteHeader />
      <div className="flex-1">{children}</div>
      <SiteFooter />
    </div>
  );
}
